import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/AuthContext";
import { GoogleLoginButton } from "@components/GoogleLoginButton";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { PageContainer } from "@components/PageContainer";

export default function Account() {
  const { t } = useTranslation();
  const { user, isAuthenticated, deleteAccount } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!isAuthenticated) {
    return (
      <PageContainer
        sx={{ maxWidth: 500, py: { xs: 6, lg: 10 }, textAlign: "center" }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            fontFamily: "'Playfair Display', serif",
            mb: 2,
          }}
        >
          {t("account.title")}
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            mb: 4,
          }}
        >
          {t("account.signInSubtitle")}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <GoogleLoginButton />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={{ maxWidth: 600, px: 3 }}>
      <Card elevation={0} sx={{ borderRadius: "12px", overflow: "hidden" }}>
        <Box
          sx={(theme) => ({
            ...(theme.palette.mode === "dark"
              ? {
                  background: `linear-gradient(145deg, ${alpha(
                    theme.palette.secondary.main,
                    0.22,
                  )} 0%, ${theme.palette.background.paper} 60%)`,
                  borderBottom: `1px solid ${alpha(
                    theme.palette.secondary.main,
                    0.2,
                  )}`,
                }
              : { bgcolor: "#FFE5D9" }),
            p: 4,
            textAlign: "center",
          })}
        >
          <Avatar
            sx={{
              width: 96,
              height: 96,
              mx: "auto",
              mb: 2,
              bgcolor: "primary.main",
              fontSize: 40,
            }}
          >
            {user?.email?.charAt(0).toUpperCase()}
          </Avatar>
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            {user?.email}
          </Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="overline"
            sx={{
              color: "text.secondary",
              fontWeight: "bold",
              mb: 2,
              display: "block",
              px: 1,
            }}
          >
            {t("account.title")}
          </Typography>

          <Box sx={{ px: 1, mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              {t("account.email")}
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
              }}
            >
              {user?.email}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Button
            startIcon={<DeleteIcon />}
            color="error"
            onClick={() => setConfirmOpen(true)}
            sx={{ px: 2 }}
          >
            {t("account.deleteAccount")}
          </Button>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={confirmOpen}
        title={t("account.deleteAccount")}
        message={t("account.deleteConfirm")}
        onConfirm={async () => {
          await deleteAccount();
          setConfirmOpen(false);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </PageContainer>
  );
}
